/**
 * postMessage Bridge — MetaDefender Cloud (iframe) → My OPSWAT (parent)
 *
 * All events share a typed envelope so the parent can safely identify and
 * route them without ambiguity with other postMessage senders.
 *
 * Dispatch rules
 * ──────────────
 * • navigation events  — always sent when embedded (route changes are state,
 *                        not interactions, so they bypass the active gate)
 * • analytics events   — only sent when the iframe is considered "active"
 *                        (the user has focused or hovered into the iframe)
 * • lifecycle events   — always sent so the parent can track the active/
 *                        inactive transition correctly
 *
 * Target origin
 * ─────────────
 * **`GATSBY_PARENT_ORIGIN`** is supplied at build time (e.g. TeamCity) — one absolute
 * parent URL (e.g. `https://my.opswat.com`). It is normalized to **`URL.origin`** for
 * `postMessage`. **`'*'` is never used**; if the variable is missing, `*`, not a valid
 * URL, or otherwise unusable, **`postMessage` is skipped** (with a one-time console
 * warning or error in production).
 */

// ─── Public types ─────────────────────────────────────────────────────────────

export type EventType = 'navigation' | 'analytics' | 'lifecycle';

export interface IframeEvent {
    /** Discriminator — lets the parent filter out unrelated postMessage senders. */
    source: 'metadefender-cloud';
    /** Schema version — bump when the envelope shape changes. */
    version: '1';
    type: EventType;
    payload: {
        /** Mirrors the GTM event name where applicable. */
        event: string;
        [key: string]: unknown;
    };
    meta: {
        /** ISO-8601 timestamp produced inside the iframe. */
        timestamp: string;
        /** Current iframe path + search (no origin — avoids leaking sensitive query params unintentionally). */
        url: string;
        /**
         * Stable per-page-load ID.  The parent uses this to correlate a burst of
         * events to a single iframe session and to deduplicate retries.
         */
        sessionId: string;
    };
}

// ─── Internal state ───────────────────────────────────────────────────────────

let _isActive = false;
let _sessionId: string | null = null;
let _listenersRegistered = false;
let _warnedInvalidParentOrigin = false;
/** Used only when Web Crypto is unavailable (tests / very old runtimes). */
let _legacySessionSeq = 0;

/**
 * Reset all internal module state.
 * Only intended for unit tests — do not call in application code.
 * @internal
 */
export const __resetStateForTesting = (): void => {
    _isActive = false;
    _sessionId = null;
    _listenersRegistered = false;
    _warnedInvalidParentOrigin = false;
    _legacySessionSeq = 0;
};

// ─── Environment helpers ──────────────────────────────────────────────────────

/** True when the page is rendered inside an iframe. */
export const isEmbedded = (): boolean => {
    if (typeof window === 'undefined') return false;
    try {
        return window.self !== window.top;
    } catch {
        // Cross-origin access to window.top throws — we ARE in an iframe.
        return true;
    }
};

/**
 * Target origin for `postMessage` — never `'*'`. Returns `null` if build-time config
 * is missing, wildcard, or not a parseable absolute URL.
 */
const getParentPostMessageOrigin = (): string | null => {
    const raw = process.env.GATSBY_PARENT_ORIGIN?.trim();
    if (!raw || raw === '*') {
        if (!_warnedInvalidParentOrigin) {
            _warnedInvalidParentOrigin = true;
            const msg =
                '[postMessageBridge] GATSBY_PARENT_ORIGIN must be an explicit parent origin ' +
                '(e.g. https://my.opswat.com). Wildcards are not allowed. Refusing to postMessage.';
            if (process.env.GATSBY_ENV === 'prod') {
                // eslint-disable-next-line no-console -- intentional misconfiguration signal in production
                console.error(msg);
            } else {
                // eslint-disable-next-line no-console -- intentional configuration warning
                console.warn(msg);
            }
        }
        return null;
    }

    try {
        const origin = new URL(raw).origin;
        if (origin === 'null') return null;
        return origin;
    } catch {
        if (!_warnedInvalidParentOrigin) {
            _warnedInvalidParentOrigin = true;
            const msg =
                '[postMessageBridge] GATSBY_PARENT_ORIGIN is not a valid absolute URL. Refusing to postMessage.';
            if (process.env.GATSBY_ENV === 'prod') {
                // eslint-disable-next-line no-console -- intentional misconfiguration signal in production
                console.error(msg);
            } else {
                // eslint-disable-next-line no-console -- intentional configuration warning
                console.warn(msg);
            }
        }
        return null;
    }
};

/** RFC 4122 v4 UUID from CSPRNG when `randomUUID` is unavailable (still avoids Math.random). */
const randomUUIDFromGetRandomValues = (): string => {
    const bytes = new Uint8Array(16);
    crypto.getRandomValues(bytes);
    bytes[6] = (bytes[6]! & 0x0f) | 0x40;
    bytes[8] = (bytes[8]! & 0x3f) | 0x80;
    const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
};

/** Unique per-load session identifier (CSPRNG when Web Crypto is available). */
const createSessionId = (): string => {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
        return crypto.randomUUID();
    }
    if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
        return randomUUIDFromGetRandomValues();
    }
    // Ancient environments only: predictable, but unique within the module instance.
    return `sess-${Date.now()}-${++_legacySessionSeq}`;
};

/** Stable per-load session identifier. */
const getSessionId = (): string => {
    if (!_sessionId) {
        _sessionId = createSessionId();
    }
    return _sessionId;
};

// ─── Activity tracking ────────────────────────────────────────────────────────

const setActive = (active: boolean): void => {
    if (_isActive === active) return;
    _isActive = active;
    // Notify the parent immediately so it can open/close its own event gate.
    dispatchToParent('lifecycle', { event: active ? 'iframe:active' : 'iframe:inactive' });
};

/**
 * Register focus / blur / pointer-entry listeners.
 * Returns a cleanup function — call it on unmount if needed.
 * Idempotent: subsequent calls are no-ops.
 */
export const initIframeActivityTracking = (): (() => void) => {
    if (typeof window === 'undefined' || !isEmbedded()) return () => undefined;
    if (_listenersRegistered) return () => undefined;

    _listenersRegistered = true;

    const onFocus = () => setActive(true);
    const onBlur = () => setActive(false);
    // Pointer entering the iframe content area counts as activation even
    // before the user clicks (so scrolling-triggered analytics still fire).
    const onPointerEnter = () => setActive(true);

    window.addEventListener('focus', onFocus);
    window.addEventListener('blur', onBlur);
    document.addEventListener('pointerenter', onPointerEnter);

    // Treat a focused iframe at load time as immediately active.
    setActive(document.hasFocus());

    return () => {
        window.removeEventListener('focus', onFocus);
        window.removeEventListener('blur', onBlur);
        document.removeEventListener('pointerenter', onPointerEnter);
        _listenersRegistered = false;
    };
};

// ─── Core dispatcher ──────────────────────────────────────────────────────────

/** Low-level dispatcher — bypasses the active gate. */
const dispatchToParent = (type: EventType, payload: { event: string; [key: string]: unknown }): void => {
    const origin = getParentPostMessageOrigin();
    if (origin === null) return;

    const message: IframeEvent = {
        source: 'metadefender-cloud',
        version: '1',
        type,
        payload,
        meta: {
            timestamp: new Date().toISOString(),
            url: window.location.pathname + window.location.search,
            sessionId: getSessionId(),
        },
    };

    window.parent.postMessage(message, origin);
};

/**
 * Send a typed event to the parent window.
 *
 * No-op when:
 *   – SSR (no window)
 *   – Not running inside an iframe
 *   – The iframe is inactive AND the event type is 'analytics'
 *     (navigation and lifecycle events bypass the active gate)
 */
export const sendToParent = (type: EventType, payload: { event: string; [key: string]: unknown }): void => {
    if (typeof window === 'undefined') return;
    if (!isEmbedded()) return;

    // Gate analytics events behind the active check; let navigation and
    // lifecycle events through unconditionally.
    if (type === 'analytics' && !_isActive) return;

    dispatchToParent(type, payload);
};
